/** Order CSV / Excel export helpers */

export const shouldIncludeByScope = (order, scope) => {
  if (scope === 'all') return true;
  const requiresShipping = Array.isArray(order.line_items)
    ? order.line_items.some(item => item?.requires_shipping !== false)
    : false;
  return scope === 'shipping' ? requiresShipping : !requiresShipping;
};

export const getExportDataByScope = (orders, scope) => {
  const baseData = orders.length > 0 ? orders : orders;
  return baseData.filter(order => shouldIncludeByScope(order, scope));
};

/** Server-side export — single streamed query (preferred). */
export async function downloadOrdersExportFromServer(databaseAPI, filters, format) {
  const fmt = format === 'excel' ? 'xlsx' : format;
  const res = await databaseAPI.downloadOrdersExport({ ...filters, format: fmt });
  if (!res.success) {
    throw new Error(res.error || 'Export failed');
  }
  const link = document.createElement('a');
  const url = URL.createObjectURL(res.blob);
  link.href = url;
  link.download = res.filename || `orders-export.${fmt === 'xlsx' ? 'xlsx' : 'csv'}`;
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  return true;
}

/**
 * @deprecated Use downloadOrdersExportFromServer — pages API and rescans filters per offset.
 */
export async function fetchAllOrdersForExport(
  databaseAPI,
  filters,
  { pageSize = 100, maxRows = 50000 } = {},
) {
  const all = [];
  let offset = 0;
  let total = null;

  while (all.length < maxRows) {
    const res = await databaseAPI.getOrdersPage({
      ...filters,
      limit: pageSize,
      offset,
    });
    if (!res.success) {
      throw new Error(res.error || 'Failed to fetch orders for export');
    }
    if (total === null) total = Number(res.total) || 0;
    const page = res.orders || [];
    if (page.length === 0) break;
    all.push(...page);

    if (res.next_offset == null || res.next_offset === '' || all.length >= total) {
      break;
    }
    offset = res.next_offset;
  }

  const truncated = total != null && total > maxRows;
  return {
    orders: truncated ? all.slice(0, maxRows) : all,
    total: total ?? all.length,
    truncated,
  };
}

const defaultFormatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const exportOrdersToCSV = ({
  orders,
  scope,
  getStatusMapping,
  alert: alertFn = window.alert,
  formatDate = defaultFormatDate,
}) => {
  const dataToExport = getExportDataByScope(orders, scope);

  if (dataToExport.length === 0) {
    alertFn('No orders to export!');
    return;
  }

  const headers = [
    'Order ID',
    'Customer Name',
    'Customer Email',
    'Total Price',
    'Currency',
    'Status',
    'Tracking Number',
    'Order Type',
    'Items',
    'Shipping Address',
    'Order Date',
    'Last Updated'
  ];

  const csvData = dataToExport.map(order => {
    const statusMapping = getStatusMapping(order.is_preorder);
    const items = order.line_items
      ? order.line_items.map(item => `${item.title} (Qty: ${item.quantity})`).join('; ')
      : '';

    const shippingAddress = order.shipping_address
      ? [
          order.shipping_address.address1 || '',
          order.shipping_address.address2 || '',
          order.shipping_address.city || '',
          order.shipping_address.province || '',
          order.shipping_address.zip || '',
          order.shipping_address.country || '',
          order.shipping_address.phone_number ? `Phone: ${order.shipping_address.phone_number}` : ''
        ].filter(Boolean).join(', ')
      : '';

    return [
      order.name || '',
      order.customer ? `${order.customer.first_name || ''} ${order.customer.last_name || ''}`.trim() : '',
      order.email || '',
      order.total_price || '',
      order.currency || '',
      statusMapping[order.custom_status] || order.custom_status || '',
      order.tracking_number || '',
      order.is_preorder ? 'Preorder' : 'Regular',
      items,
      shippingAddress,
      formatDate(order.created_at),
      new Date().toLocaleDateString()
    ];
  });

  const csvContent = [headers, ...csvData]
    .map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
    .join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `orders-export-${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  alertFn(`✅ Successfully exported ${dataToExport.length} orders to CSV file!`);
};

export const exportOrdersToExcel = ({
  orders,
  scope,
  getStatusMapping,
  alert: alertFn = window.alert,
  formatDate = defaultFormatDate,
}) => {
  const dataToExport = getExportDataByScope(orders, scope);

  if (dataToExport.length === 0) {
    alertFn('No orders to export!');
    return;
  }

  const excelData = dataToExport.map(order => {
    const statusMapping = getStatusMapping(order.is_preorder);
    const items = order.line_items
      ? order.line_items.map(item => `${item.title} (Qty: ${item.quantity})`).join('\n')
      : '';

    const shippingAddress = order.shipping_address
      ? [
          order.shipping_address.address1 || '',
          order.shipping_address.address2 || '',
          order.shipping_address.city || '',
          order.shipping_address.province || '',
          order.shipping_address.zip || '',
          order.shipping_address.country || ''
        ].filter(Boolean).join(', ')
      : '';

    return {
      'Order ID': order.name || '',
      'Customer Name': order.customer ? `${order.customer.first_name || ''} ${order.customer.last_name || ''}`.trim() : '',
      'Customer Email': order.email || '',
      'Total Price': order.total_price || '',
      'Currency': order.currency || '',
      'Status': statusMapping[order.custom_status] || order.custom_status || '',
      'Tracking Number': order.tracking_number || '',
      'Order Type': order.is_preorder ? 'Preorder' : 'Regular',
      'Items': items,
      'Shipping Address': shippingAddress,
      'Phone': order.shipping_address?.phone || '',
      'Order Date': formatDate(order.created_at),
      'Last Updated': new Date().toLocaleDateString()
    };
  });

  const excelContent = `
      <table>
        <thead>
          <tr>
            ${Object.keys(excelData[0] || {}).map(key => `<th>${key}</th>`).join('')}
          </tr>
        </thead>
        <tbody>
          ${excelData.map(row =>
            `<tr>${Object.values(row).map(cell => `<td>${String(cell).replace(/\n/g, '<br/>')}</td>`).join('')}</tr>`
          ).join('')}
        </tbody>
      </table>
    `;

  const blob = new Blob([excelContent], {
    type: 'application/vnd.ms-excel;charset=utf-8;'
  });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `orders-export-${new Date().toISOString().split('T')[0]}.xls`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  alertFn(`✅ Successfully exported ${dataToExport.length} orders to Excel file!`);
};
